import './Prices.css';

import { ColumnContent, SortableList } from '../../../../../components/SortableList/SortableList';
import { Event, EventProductVariant, PricingCategory } from '../../../../../model/event';
import React, { useState } from 'react';
import { applyTax, loadPricingCategories, pricingCategoryTotal } from '../../../../../model/price';
import {
  dateBetween,
  formatDateTime,
  formatNumber,
  formatPercentage,
  removeTax,
  useReloadKey,
} from '../../../../../util/util';

import { Card } from '../../../../../components/Card/Card';
import { ControlledModal } from '../../../../../components/Modal/ControlledModal';
import { Icon } from '../../../../../components/Icon/Icon';
import { LoadingPage } from '../../../../../components/LoadingPage/LoadingPage';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';
import { PricingMatrix } from './PricingMatrix';
import { t } from '../../../../../locales/translate';
import { useRemoteData } from '../../../../../model/data';
import { runTicketPricingExport } from '../../../../../model/exports';
import { Stack } from '../../../../../components/Stack/Stack';
import { useIsAdmin } from '../../../../../model/membership';
import { useQueryHandler } from '../../../../../model/graphql';
import { graphql } from '../../../../../generated';

const TAX_RATES = graphql(`
  query TaxRates {
    taxRates {
      nodes {
        country {
          countryCode
          name
        }
        amount
      }
    }
  }
`);

function displayNetGross(value?: number, tax?: number, brutOnly?: boolean): string {
  if (brutOnly) {
    return formatNumber(applyTax(value ?? 0, tax ?? 0), 2) ?? '';
  }
  return `${formatNumber(value ?? 0, 6)} / ${formatNumber(applyTax(value ?? 0, tax ?? 0), 2)}`;
}

function currencyDescription(title: string, brutOnly?: boolean): string {
  return [title, brutOnly ? t.model.price.currencyGross : t.model.price.currencyWithVat].join(' ');
}

export function Prices({ event, local = false }: { event: Event; local?: boolean }): JSX.Element {
  const admin = useIsAdmin();
  const [pricingCategoryForModal, setPricingCategoryForModal] = useState<PricingCategory | undefined>(undefined);
  const [categoriesKey, reloadCategories] = useReloadKey();
  const [categories, loading] = useRemoteData(
    () => loadPricingCategories({ event, local }),
    (db) => db.table('pricingCategories').where({ eventId: event.id }),
    [event.id, local ? 'local' : 'system', categoriesKey.toString()]
  );
  const { data } = useQueryHandler(TAX_RATES, {});
  const germanyTaxRate = data?.taxRates.nodes.find((e) => e.country?.countryCode === 'DE')?.amount || 0.19;

  if (loading) {
    return <LoadingPage />;
  }

  function handleOnClose(): void {
    setPricingCategoryForModal(undefined);
    reloadCategories();
  }

  function displaySellerNetGross(productVariant: EventProductVariant): string {
    if (productVariant.sellerFee !== null) {
      // net value is returned from server
      return `${formatNumber(productVariant.sellerFee, 6)} / ${formatNumber(
        applyTax(productVariant.sellerFee, germanyTaxRate),
        2
      )}`;
    }

    const grossSum = (productVariant.presaleFee + productVariant.systemFee) * (1 + productVariant.taxRate);
    return `${formatNumber(removeTax(grossSum, germanyTaxRate), 6)} / ${formatNumber(grossSum, 2)}`;
  }

  return (
    <>
      {admin && (
        <Stack justify="right">
          <Icon
            onClick={() => runTicketPricingExport({ eventId: event.id })}
            name="download"
            tooltip={t.page.turnover.downloadAsPdf}
          />
        </Stack>
      )}

      {categories && categories.length === 0 && (
        <div>{t.errors.noItemsforThisCollection(t.model.price._plural, t.model.event._name)}</div>
      )}
      {[...categories]
        .sort((a, b) => (a.name < b.name ? -1 : 1))
        .map((category) => {
          const columns: ColumnContent<EventProductVariant>[] = [];
          const priceParts = category.productVariants[0]?.priceParts || [];

          columns.push({
            dataKey: 'id',
            cellRenderer: (row) => <ColumnLabel productVariant={row} local={!!local} />,
            align: 'right',
            renderedKeyOverride: (row) => row.id,
          });
          columns.push({
            dataKey: 'basePrice',
            cellRenderer: (row) => displayNetGross(row.basePrice, row.taxRate, local),
            label: currencyDescription(t.model.price.basePrice, local),
            align: 'right',
            renderedKeyOverride: (row) => row.id,
          });
          columns.push({
            dataKey: 'taxRate',
            cellRenderer: (row) => formatPercentage(row.taxRate),
            label: t.model.price.vat,
            align: 'right',
            renderedKeyOverride: (row) => row.id,
          });
          if (!local) {
            columns.push({
              dataKey: 'presaleFee',
              cellRenderer: (row) => displayNetGross(row.presaleFee, row.taxRate, local),
              label: currencyDescription(t.model.price.presaleFee, local),
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
            columns.push({
              dataKey: 'systemFee',
              cellRenderer: (row) => displayNetGross(row.systemFee, row.taxRate, local),
              label: currencyDescription(t.model.price.systemFee, local),
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
          }
          priceParts.forEach((pp) =>
            columns.push({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              dataKey: pp.description as any,
              label: pp.description + ' price part',
              cellRenderer: (row: EventProductVariant) =>
                displayNetGross(
                  row.priceParts.find((e) => e.description === pp.description)?.price,
                  row.taxRate,
                  local
                ),
              align: 'right',
              keyOverride: pp.description,
            })
          );
          columns.push({
            dataKey: 'physical',
            cellRenderer: (row) => displayNetGross(pricingCategoryTotal(row), row.taxRate, local),
            label: <div className="pricing-table__total">{currencyDescription(t.general.total, local)}</div>,
            align: 'right',
            renderedKeyOverride: (row) => row.id,
          });
          if (!local) {
            columns.push({
              dataKey: 'refundDeduction',
              cellRenderer: (row) => displayNetGross(row.refundDeduction, row.taxRate, local),
              label: currencyDescription(t.model.price.refundDeduction, local),
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
            columns.push({
              dataKey: 'sellerFee',
              cellRenderer: (row) => displaySellerNetGross(row),
              label: currencyDescription(t.model.price.resaleSellerFee, local),
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
            columns.push({
              dataKey: 'buyerFee',
              cellRenderer: (row) => displayNetGross(row.buyerFee || 0, germanyTaxRate, local),
              label: currencyDescription(t.model.price.resaleBuyerFee, local),
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
            columns.push({
              dataKey: 'published',
              cellRenderer: (row) => (
                <span className="active-range__container">
                  <div>
                    <div className="active-range__value">{formatDateTime(row.activeFrom)}</div>
                    <div className="active-range__value">{formatDateTime(row.activeUntil)}</div>
                  </div>
                  {dateBetween(new Date(), {
                    start: row.activeFrom,
                    end: row.activeUntil,
                  }) ? (
                    <Icon name="check" className="check-mark--green" />
                  ) : (
                    <Icon name="plus" className="cross--red" />
                  )}
                </span>
              ),
              label: t.model.status.active,
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
            columns.push({
              dataKey: 'private',
              cellRenderer: (row) => (row.private ? <Icon name="check" /> : '-'),
              label: t.model.productVariant.private,
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
            columns.push({
              dataKey: 'resaleAllowed',
              cellRenderer: (row) => (row.resaleAllowed ? <Icon name="check" /> : '-'),
              label: t.model.productVariant.resaleAllowed,
              align: 'right',
              renderedKeyOverride: (row) => row.id,
            });
          }
          return (
            <Card
              key={category.name}
              title={category.name}
              actions={
                <PermissionButton
                  name="edit"
                  model={event}
                  permission={local ? 'update_local_pricing_categories' : 'update_pricing_categories'}
                  onClick={() => {
                    setPricingCategoryForModal(category);
                  }}
                />
              }
              fullbleed
            >
              <SortableList
                switchRowsAndColumns
                items={category.productVariants}
                rightSpacing="100%"
                columns={columns}
              />
            </Card>
          );
        })}
      <ControlledModal
        title={pricingCategoryForModal && pricingCategoryForModal.name}
        open={!!pricingCategoryForModal}
        model={pricingCategoryForModal}
        onClose={handleOnClose}
      >
        {(controller) => <PricingMatrix controller={controller} simplified={local} germanyTaxRate={germanyTaxRate} />}
      </ControlledModal>
    </>
  );
}

function ColumnLabel({ productVariant, local }: { productVariant: EventProductVariant; local: boolean }): JSX.Element {
  const name = [
    !local && (productVariant.physical ? t.model.ticket.hardticket : t.model.ticket.onlineticket),
    productVariant.description,
  ]
    .filter(Boolean)
    .join(' / ');
  return (
    <div className="price-card__column-title">
      <div>{productVariant.sku}</div>
      <div>{name}</div>
    </div>
  );
}
